import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, ChevronRight, ChevronDown, AlertTriangle, Users, DollarSign, TrendingUp,
  CalendarClock, Flame, Snowflake, CalendarDays, Search, Download, Sun, RotateCcw, Tag, Send, type LucideIcon,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/ui/PageHeader";
import { displayCustomerName } from "@/lib/format";
import {
  useExpiringAgreements, useAgreementKpis, useLapsedAgreements, useOwedUnscheduled, useUpcomingVisits,
  type ExpiringAgreement, type LapsedAgreement, type OwedAgreement, type UpcomingVisitRow,
} from "./api";
import { useSegments } from "@/features/reports/api";
import { SeasonBoard } from "./SeasonBoard";
import { PlanOffersView, useOpenOfferCount } from "./PlanOffersView";
import { useSeasonVisits, currentSeasonKey, summarize } from "./season";

// ============================================================================
// AGR-02 — Expiring-agreements renewal worklist. Work the list -> renew.
// ============================================================================

const WINDOWS = [30, 60, 90] as const;

// Entrance motion — the KPI tiles rise + stagger in (DS-MODERN-01).
const kpiContainer = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
const kpiItem = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } } };

// Worklist rows flow in top-to-bottom. Per-row delay is capped so a long list
// still finishes fast (impeccable pass, 2026-05-31). ease-out-expo, no bounce.
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
const rowMotion = (i: number) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.2, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.03, 0.3) },
});

function money(v: number | null): string {
  if (v === null) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
function fmt(d: string | null): string {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
}

function Kpi({ label, value, sub, accent, Icon }: {
  label: string; value: string; sub?: string; accent?: boolean; Icon: LucideIcon;
}) {
  return (
    <motion.div
      variants={kpiItem}
      className="group rounded-2xl border border-hairline bg-white p-5 shadow-e1 ring-1 ring-transparent transition-all duration-200 ease-out-quart hover:-translate-y-0.5 hover:shadow-e2 hover:ring-brand-100 motion-reduce:hover:translate-y-0"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className={cn(
          "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
          accent ? "bg-amber-50 text-amber-600 group-hover:bg-amber-100" : "bg-brand-50 text-brand-600 group-hover:bg-brand-100",
        )}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className={cn("mt-2.5 text-[26px] leading-none font-bold tracking-tight tabular-nums [font-family:var(--font-display)] xl:text-[30px]", accent ? "text-amber-700" : "text-slate-900")}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-slate-500">{sub}</div>}
    </motion.div>
  );
}

export function AgreementsWorklistView() {
  const navigate = useNavigate();
  // MEMBERSHIPS-02 P3: the Season board is the Memberships home. Owed visits
  // and Upcoming folded into it; Renewals and Win-back stay as tabs.
  const [view, setView] = useState<string>("season");
  const [windowDays, setWindowDays] = useState<number>(60);
  const [planTerm, setPlanTerm] = useState<string>("all");
  const [zone, setZone] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [sortByValue, setSortByValue] = useState<boolean>(false);
  const [upcomingWindow, setUpcomingWindow] = useState<string>("180");
  const [upcomingType, setUpcomingType] = useState<string>("all");
  const { data, isLoading } = useExpiringAgreements(windowDays);
  const { data: kpis } = useAgreementKpis();
  const { data: lapsedData, isLoading: lapsedLoading } = useLapsedAgreements();
  const { data: owedData, isLoading: owedLoading } = useOwedUnscheduled();
  const { data: upcomingData, isLoading: upcomingLoading } = useUpcomingVisits(upcomingWindow === "all" ? null : parseInt(upcomingWindow, 10));
  const { data: seasonRows } = useSeasonVisits(currentSeasonKey());
  const seasonOwed = seasonRows ? summarize(seasonRows).owed - summarize(seasonRows).done : null;
  // SEG-01b — saved segments as tabs. A segment's WORKSPACE is the Map & batch
  // view (locked 2026-06-03: "I can't ever see using the list over map &
  // batch"), so clicking a segment tab goes straight there — the member list
  // lives inside that workspace as the List lens.
  const { data: segments } = useSegments("agreement");
  // PLAN-OFFER-01 slice D: every plan waiting on a yes, company-wide (the 360 shows the same rows).
  const openOffers = useOpenOfferCount();

  const open = (custno: string) => navigate(`/customers/${custno}?tab=agreements&from=agreements`);

  // Zone options pooled from all three lists so the dropdown is stable across tabs.
  const zoneOptions = Array.from(
    new Set([...(data ?? []), ...(lapsedData ?? []), ...(owedData ?? []), ...(upcomingData ?? [])].map((r) => r.city).filter(Boolean) as string[]),
  ).sort();

  // Name / account search on top of the shared filters (works on every tab).
  const bySearch = <T extends { customer_fullname: string | null; custno: string }>(items: T[]): T[] => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.customer_fullname ?? "").toLowerCase().includes(q) || r.custno.toLowerCase().includes(q));
  };
  const rows = bySearch(applyFilters(data ?? [], planTerm, zone, sortByValue));
  const atRisk = rows.reduce((sum, r) => sum + (r.contr_amt ?? 0), 0);
  const lapsed = bySearch(applyFilters(lapsedData ?? [], planTerm, zone, sortByValue));
  const lapsedValue = lapsed.reduce((s, r) => s + (r.contr_amt ?? 0), 0);
  const owed = bySearch(applyFilters(owedData ?? [], planTerm, zone, sortByValue));
  const owedValue = owed.reduce((s, r) => s + (r.contr_amt ?? 0), 0);
  const upcomingBase = bySearch(applyFilters(upcomingData ?? [], planTerm, zone, sortByValue));
  const upcoming = upcomingType === "all"
    ? upcomingBase
    : upcomingBase.filter((r) => (upcomingType === "furnace" ? r.visitLabel.startsWith("Furnace") : r.visitLabel.startsWith("A/C")));

  // One-click CSV of whatever list is on screen (filters + search applied).
  const exportCurrent = () => {
    const esc = (v: string | number | null | undefined) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = ["customer", "custno", "plan", "value", "city", "date", "detail"];
    let body: (string | number | null)[][] = [];
    if (view === "renewals") body = rows.map((r) => [r.customer_fullname, r.custno, r.contr_per ?? r.agrmt_type, r.contr_amt, r.city, r.expire_date, `${r.days_left}d left`]);
    if (view === "lapsed") body = lapsed.map((r) => [r.customer_fullname, r.custno, r.contr_per ?? r.agrmt_type, r.contr_amt, r.city, r.expire_date, `lapsed ${r.days_lapsed}d`]);
    if (view === "owed") body = owed.map((r) => [r.customer_fullname, r.custno, r.contr_per ?? r.agrmt_type, r.contr_amt, r.city, r.last_srvc, "owed · nothing booked"]);
    if (view === "upcoming") body = upcoming.map((r) => [r.customer_fullname, r.custno, r.contr_per ?? r.agrmt_type, r.contr_amt, r.city, r.next_post_date, r.visitLabel]);
    const csv = [head, ...body].map((r) => r.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `memberships-${view}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mx-auto max-w-7xl p-8"
    >
      <PageHeader
        icon={ShieldCheck}
        title="Memberships"
        description="Your recurring-revenue book: the numbers at a glance, this season's checks to get done, then the renewal lists."
      />

      {/* KPI header (AGR-06) */}
      {kpis && (
        <motion.div
          variants={kpiContainer}
          initial="hidden"
          animate="show"
          className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
        >
          <Kpi Icon={Users} label="Active members" value={kpis.members.toLocaleString()} sub={`${kpis.active_agreements.toLocaleString()} agreements`} />
          <Kpi Icon={DollarSign} label="Recurring revenue" value={money(kpis.annualized_recurring_rev)} sub="per year" />
          <Kpi Icon={TrendingUp} label="Retention (est.)" value={`${kpis.retention_est}%`} sub={`vs ${kpis.lapsed_12mo} lapsed`} />
          <Kpi Icon={CalendarClock} label="Expiring (90 days)" value={kpis.expiring_90d.toLocaleString()} sub="up for renewal" />
          <Kpi Icon={AlertTriangle} label="At risk (90 days)" value={money(kpis.value_expiring_90d)} sub="if not renewed" accent />
        </motion.div>
      )}

      {/* Worklist switcher — SPREAD-01 (Seth approved the 360 header, "work
          on what's next"): the sections wear the SAME icon tab bar the 360
          uses — 48px tabs, counts, sliding ink, full-row stretch. One nav
          idiom everywhere. */}
      <div className="mb-4">
        <Tabs
          variant="bar"
          value={view}
          onChange={(k) => {
            if (k.startsWith("seg:")) {
              navigate(`/agreements/planner?seg=${k.slice(4)}`);
              return;
            }
            setView(k);
          }}
          items={[
            { key: "season", label: "This Season", icon: Sun, count: seasonOwed },
            { key: "renewals", label: "Renewals", icon: CalendarClock, count: kpis?.expiring_90d ?? null },
            { key: "lapsed", label: "Win-back", icon: RotateCcw, count: lapsedData?.length ?? null },
            { key: "offers", label: "Offers", icon: Send, count: openOffers, countTone: openOffers ? "amber" : "neutral" },
            ...(segments ?? []).map((s) => ({ key: `seg:${s.id}`, label: s.name, icon: Tag, count: null as number | null })),
          ]}
        />
      </div>

      {view === "season" && <SeasonBoard />}
      {view === "offers" && <PlanOffersView />}

      {/* Filter bar (shared by the list tabs) + per-view summary */}
      {view !== "season" && view !== "offers" && (
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or account #…"
              className="h-8 w-44 rounded-lg border border-hairline bg-white pl-8 pr-2 text-xs text-slate-700 transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          {view === "renewals" && (
            <div className="inline-flex h-8 items-center rounded-full border border-hairline bg-slate-50 p-0.5">
              {WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWindowDays(w)}
                  className={cn(
                    "relative flex h-full items-center rounded-full px-3.5 text-xs font-medium transition-colors duration-150 ease-out-quart",
                    windowDays === w ? "text-slate-900" : "text-slate-500 hover:text-slate-900",
                  )}
                >
                  {windowDays === w && (
                    <motion.span
                      layoutId="agr-window-pill"
                      className="absolute inset-0 rounded-full bg-white shadow-e1"
                      transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
                    />
                  )}
                  <span className="relative z-10">Next {w} days</span>
                </button>
              ))}
            </div>
          )}
          {view === "upcoming" && (
            <>
              <FilterSelect
                value={upcomingWindow}
                onChange={setUpcomingWindow}
                options={[["90", "Next 3 months"], ["180", "Next 6 months"], ["365", "Next 12 months"], ["all", "All upcoming"]]}
              />
              <FilterSelect
                value={upcomingType}
                onChange={setUpcomingType}
                options={[["all", "All checks"], ["furnace", "Furnace only"], ["ac", "A/C only"]]}
              />
            </>
          )}
          <FilterSelect
            value={planTerm}
            onChange={setPlanTerm}
            options={[["all", "All plans"], ["1 Year", "1-Year"], ["2 Year", "2-Year"], ["3 Year", "3-Year"], ["5 Year", "5-Year"]]}
          />
          {zoneOptions.length > 0 && (
            <FilterSelect
              value={zone}
              onChange={setZone}
              options={[["all", "All zones"], ...zoneOptions.map((c) => [c, c] as [string, string])]}
            />
          )}
          <FilterSelect
            value={sortByValue ? "value" : "default"}
            onChange={(v) => setSortByValue(v === "value")}
            options={[["default", "Default order"], ["value", "Highest value"]]}
          />
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-600">
          {view === "renewals" && !isLoading && rows.length > 0 && (
            <span><span className="font-semibold text-slate-900 tabular-nums">{rows.length}</span> expiring · <span className="font-semibold text-slate-900 tabular-nums">{money(atRisk)}</span> at risk</span>
          )}
          {/* AGR renewal LOOP (2026-08-18): the list isn't just for calls anymore —
              the comms engine queues each expiring member a renewal email
              automatically. This chip is the visibility + the door. */}
          {view === "renewals" && (
            <button
              type="button"
              onClick={() => navigate("/automations")}
              title="Each expiring member gets a renewal email queued automatically (30 days out; autopay plans skip). Review the exact messages in Comms & Automations."
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Renewal emails: automatic
            </button>
          )}
          {view === "lapsed" && !lapsedLoading && lapsed.length > 0 && (
            <span><span className="font-semibold text-slate-900 tabular-nums">{lapsed.length}</span> lapsed · <span className="font-semibold text-slate-900 tabular-nums">{money(lapsedValue)}</span> to win back</span>
          )}
          {view === "owed" && !owedLoading && owed.length > 0 && (
            <span><span className="font-semibold text-slate-900 tabular-nums">{owed.length}</span> owed · <span className="font-semibold text-slate-900 tabular-nums">{money(owedValue)}</span> unbooked</span>
          )}
          {view === "upcoming" && !upcomingLoading && upcoming.length > 0 && (
            <span><span className="font-semibold text-slate-900 tabular-nums">{upcoming.length}</span> coming due · the season's tune-ups to schedule</span>
          )}
          {/* PLANNER-01 — the map + day-plan workspace (segments land there via their tab too). */}
          {view === "upcoming" && upcoming.length > 0 && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/agreements/planner?list=upcoming&window=${upcomingWindow}&type=${upcomingType}`,
                )
              }
              title="Map this list, build saved day plans, and book members onto the board"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Tune-Up Planner
            </button>
          )}
          {view === "owed" && owed.length > 0 && (
            <button
              type="button"
              onClick={() => navigate("/agreements/planner?list=owed")}
              title="Map the owed list, build saved day plans, and book members onto the board"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Tune-Up Planner
            </button>
          )}
          <button
            type="button"
            onClick={exportCurrent}
            title="Download this list (filters + search applied) as a CSV"
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
        </div>
      </div>
      )}

      {view === "renewals" && (
        isLoading ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <EmptyState title={`Nothing expiring in the next ${windowDays} days`} />
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {rows.map((r, i) => <Row key={`${r.custno}-${r.locno}-${r.agrmt_no}`} r={r} i={i} onOpen={() => open(r.custno)} />)}
            </ul>
          </Card>
        )
      )}

      {view === "lapsed" && (
        lapsedLoading ? (
          <ListSkeleton />
        ) : lapsed.length === 0 ? (
          <EmptyState title="No recently lapsed agreements" sub="Everyone who lapsed in the last year has renewed or been re-signed." />
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {lapsed.map((r, i) => <LapsedRow key={`${r.custno}-${r.locno}-${r.agrmt_no}`} r={r} i={i} onOpen={() => open(r.custno)} />)}
            </ul>
          </Card>
        )
      )}

      {view === "owed" && (
        owedLoading ? (
          <ListSkeleton />
        ) : owed.length === 0 ? (
          <EmptyState title="No owed-but-unbooked visits" sub="Active members are either current or already on the schedule." />
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {owed.map((r, i) => <OwedRow key={`${r.custno}-${r.locno}-${r.agrmt_no}`} r={r} i={i} onOpen={() => open(r.custno)} />)}
            </ul>
          </Card>
        )
      )}

      {view === "upcoming" && (
        upcomingLoading ? (
          <ListSkeleton />
        ) : upcoming.length === 0 ? (
          <EmptyState title="No upcoming agreement visits" sub="No active members have a next visit posted in range." />
        ) : (
          <Card className="overflow-hidden">
            <ul className="divide-y divide-slate-100">
              {upcoming.map((r, i) => <UpcomingRow key={`${r.custno}-${r.locno}-${r.agrmt_no}`} r={r} i={i} onOpen={() => open(r.custno)} />)}
            </ul>
          </Card>
        )
      )}

    </motion.div>
  );
}

// Generic client-side filter/sort shared by all three worklists.
function applyFilters<T extends { contr_per: string | null; contr_amt: number | null; city?: string | null }>(
  items: T[], planTerm: string, zone: string, sortByValue: boolean,
): T[] {
  let out = items;
  if (planTerm !== "all") out = out.filter((i) => (i.contr_per ?? "") === planTerm);
  if (zone !== "all") out = out.filter((i) => (i.city ?? "") === zone);
  if (sortByValue) out = [...out].sort((a, b) => (b.contr_amt ?? 0) - (a.contr_amt ?? 0));
  return out;
}

// Styled native select: keeps native a11y + the OS option list, but the trigger
// reads as a designed control (hairline, custom chevron, matched height).
function FilterSelect({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-8 text-xs font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      >
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

// Loading state shaped like the real rows (pill + name + sub) so the layout
// doesn't jump when data arrives.
function ListSkeleton() {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {Array.from({ length: 6 }).map((_, i) => (
          <li key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-5 w-20 flex-shrink-0 animate-pulse rounded-full bg-slate-100" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3.5 w-40 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-64 max-w-full animate-pulse rounded bg-slate-100/80" />
            </div>
            <div className="h-4 w-4 flex-shrink-0 animate-pulse rounded bg-slate-100" />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function EmptyState({ title, sub }: { title: string; sub?: string }) {
  return (
    <Card className="p-10 text-center">
      <ShieldCheck className="mx-auto h-10 w-10 text-emerald-500" />
      <h3 className="mt-3 text-sm font-medium text-slate-900">{title}</h3>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </Card>
  );
}

function OwedRow({ r, i, onOpen }: { r: OwedAgreement; i: number; onOpen: () => void }) {
  const last = r.last_srvc;
  let lastLabel = "no service on record";
  if (last) {
    const days = Math.round((Date.now() - new Date(last + "T00:00:00").getTime()) / 86400000);
    lastLabel = `last service ${Math.round(days / 30)}mo ago`;
  }
  return (
    <motion.li
      {...rowMotion(i)}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <div className="w-28 flex-shrink-0">
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          <AlertTriangle className="h-3 w-3" /> owed
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {displayCustomerName(null, r.customer_fullname) || r.custno}
        </div>
        <div className="truncate text-xs text-slate-500">
          {r.contr_per ?? r.agrmt_type ?? "Plan"} · {money(r.contr_amt)} · {lastLabel} · nothing booked
        </div>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-all duration-150 ease-out-quart group-hover:translate-x-0.5 group-hover:text-brand-500" />
    </motion.li>
  );
}

function UpcomingRow({ r, i, onOpen }: { r: UpcomingVisitRow; i: number; onOpen: () => void }) {
  const months = Math.round(r.days_until / 30);
  const when = r.days_until <= 45 ? `in ${r.days_until}d` : `in ~${months}mo`;
  const Icon = r.visitLabel.startsWith("Furnace") ? Flame : r.visitLabel.startsWith("A/C") ? Snowflake : CalendarClock;
  const warm = r.visitLabel.startsWith("Furnace");
  return (
    <motion.li
      {...rowMotion(i)}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <div className="flex w-28 flex-shrink-0 items-center gap-2">
        <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", warm ? "bg-orange-50 text-orange-600" : "bg-brand-50 text-brand-600")}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-xs font-medium text-slate-600">{when}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {displayCustomerName(null, r.customer_fullname) || r.custno}
        </div>
        <div className="truncate text-xs text-slate-500">
          {r.visitLabel} · {fmt(r.next_post_date)} · {r.contr_per ?? r.agrmt_type ?? "Plan"}{r.city ? ` · ${r.city}` : ""}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-all duration-150 ease-out-quart group-hover:translate-x-0.5 group-hover:text-brand-500" />
    </motion.li>
  );
}

function LapsedRow({ r, i, onOpen }: { r: LapsedAgreement; i: number; onOpen: () => void }) {
  const months = Math.round(r.days_lapsed / 30);
  const ago = r.days_lapsed < 45 ? `${r.days_lapsed}d ago` : `${months}mo ago`;
  return (
    <motion.li
      {...rowMotion(i)}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <div className="w-28 flex-shrink-0">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
          lapsed {ago}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {displayCustomerName(null, r.customer_fullname) || r.custno}
        </div>
        <div className="truncate text-xs text-slate-500">
          {r.contr_per ?? r.agrmt_type ?? "Plan"} · {money(r.contr_amt)} · expired {fmt(r.expire_date)}
          {r.last_srvc ? ` · last service ${fmt(r.last_srvc)}` : ""}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-all duration-150 ease-out-quart group-hover:translate-x-0.5 group-hover:text-brand-500" />
    </motion.li>
  );
}

function Row({ r, i, onOpen }: { r: ExpiringAgreement; i: number; onOpen: () => void }) {
  const urgent = r.days_left <= 14;
  const soon = r.days_left <= 30;
  return (
    <motion.li
      {...rowMotion(i)}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
    >
      <div className="w-28 flex-shrink-0">
        <span className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
          urgent ? "bg-red-50 text-red-700" : soon ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600",
        )}>
          {urgent && <AlertTriangle className="h-3 w-3" />}
          {r.days_left}d left
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {displayCustomerName(null, r.customer_fullname) || r.custno}
        </div>
        <div className="truncate text-xs text-slate-500">
          {r.contr_per ?? r.agrmt_type ?? "Plan"} · {money(r.contr_amt)} · expires {fmt(r.expire_date)}
          {r.last_srvc ? ` · last service ${fmt(r.last_srvc)}` : ""}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-all duration-150 ease-out-quart group-hover:translate-x-0.5 group-hover:text-brand-500" />
    </motion.li>
  );
}
